import {
  MeasurementName,
  type VitalsResultMeasures,
} from '@quromedical/models';
import { getDomain } from 'components/graph';
import {
  colors,
  domains,
  gradientStops,
  titles,
  units,
  valueFormatters,
} from 'core/vitals';
import { sort } from 'd3-array';
import {
  getExpandedDomain,
  SkeletonProvider,
  Text,
  VitalCard,
} from 'design-system';
import { Grid } from 'design-system/layout';
import { useLayoutDimensions } from 'hooks/useLayoutDimensions';
import type React from 'react';
import { View } from 'react-native';
import { strings } from 'strings';

interface VitalsSectionProps {
  data?: VitalsResultMeasures;
  loading?: boolean;
}

const placeholderMeasures = [
  MeasurementName.Battery,
  MeasurementName.Battery,
  MeasurementName.Battery,
];

const Loading: React.FC = () => (
  <SkeletonProvider loading>
    <Grid>
      {placeholderMeasures.map((_, i) => (
        <VitalCard
          identifier=""
          key={i}
          initialWidth={0}
          title=""
          color="text-disabled"
          hideSummary
        />
      ))}
    </Grid>
  </SkeletonProvider>
);

export const VitalsSection: React.FC<VitalsSectionProps> = ({
  data = {},
  loading = false,
}) => {
  const { onLayout, layout } = useLayoutDimensions({});

  const availableMeasures = Object.values(MeasurementName).filter(
    (measureName) => data[measureName]?.length,
  );

  if (loading) {
    return <Loading />;
  }

  if (!availableMeasures?.length) {
    return (
      <Text variant="page-heading">{strings.PatientPrintTitleNoVitals}</Text>
    );
  }

  return (
    <View onLayout={onLayout}>
      <Grid>
        {availableMeasures.map((measureName, i) => {
          const vitals = data[measureName]?.map((d) => ({
            x: d.ts,
            y: d.value,
          }));

          const graphData = sort(vitals || [], (d) => d.x);

          const measureDomain = domains[measureName];
          const dataDomain = getDomain(graphData);

          const yDomain =
            dataDomain.y &&
            getExpandedDomain(
              measureDomain || dataDomain.y,
              graphData.map((d) => d.y),
            );

          return (
            <VitalCard
              key={`${measureName}-${i}`}
              identifier={measureName}
              initialWidth={layout.width}
              yDomain={yDomain}
              title={titles[measureName] || measureName}
              data={graphData}
              gradient={gradientStops[measureName]}
              valueFormatter={valueFormatters[measureName]}
              unit={units[measureName]}
              color={colors[measureName] || 'text-disabled'}
              hideSummary
            />
          );
        })}
      </Grid>
    </View>
  );
};
